import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Eye, EyeOff, Mail, Lock, ArrowLeft } from "lucide-react";

import { API_URL } from "@/utils/api";

export default function LoginPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [redirectTo, setRedirectTo] = useState(null);

  // Handle redirect via useEffect to work better with iOS
  useEffect(() => {
    if (redirectTo) {
      // Small delay to ensure state is fully updated
      const timer = setTimeout(() => {
        window.location.href = redirectTo;
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [redirectTo]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",  // Important for cookies on mobile
        body: JSON.stringify({ email, password })
      });
      
      // Get the response text first
      const responseText = await response.text();
      
      // Try to parse as JSON
      let data;
      try {
        data = JSON.parse(responseText);
      } catch (parseError) {
        console.error("Failed to parse response:", responseText);
        throw new Error("Failed to process server response");
      }
      
      // Check if email verification is required (403 status)
      if (response.status === 403 && data.requires_verification) {
        toast.warning("Please verify your email first");
        setRedirectTo(`/verify-email-pending?email=${encodeURIComponent(email)}`);
        return;
      }
      
      // Handle other non-OK responses
      if (!response.ok) {
        throw new Error(data.detail || "Login failed");
      }
      
      // Success - store token with multiple attempts for mobile reliability
      const token = data.access_token;
      
      // Try to store token multiple times for mobile Safari reliability
      for (let i = 0; i < 3; i++) {
        try {
          localStorage.setItem("auth_token", token);
          // Also try sessionStorage as fallback
          sessionStorage.setItem("auth_token_backup", token);
          break;
        } catch (storageError) {
          console.error(`Storage attempt ${i + 1} failed:`, storageError);
          await new Promise(r => setTimeout(r, 50));
        }
      }
      
      // Verify token was stored
      const storedToken = localStorage.getItem("auth_token") || sessionStorage.getItem("auth_token_backup");
      if (!storedToken) {
        console.error("Failed to store auth token in any storage");
        // Try one more time with a delay
        await new Promise(r => setTimeout(r, 100));
        localStorage.setItem("auth_token", token);
      }
      
      toast.success("Welcome back!");
      
      // Determine redirect path
      let redirectPath = "/dashboard";
      if (data.user.role === "admin") redirectPath = "/admin";
      else if (data.user.role === "finance") redirectPath = "/finance";
      
      // Longer delay for mobile browsers to ensure storage sync
      await new Promise(resolve => setTimeout(resolve, 200));
      
      // Set redirect state - useEffect will handle the actual redirect
      setRedirectTo(redirectPath);
    } catch (err) {
      toast.error(err.message);
      setLoading(false);
    }
    // Don't set loading to false on success - we're redirecting
  };

  // Show redirecting state
  if (redirectTo) {
    return (
      <div className="min-h-screen flex items-center justify-center font-['Inter'] bg-white">
        <div className="text-center px-8">
          <div className="w-16 h-16 border-4 border-[#BFFF00] border-t-transparent rounded-full animate-spin mx-auto mb-6"></div>
          <h2 className="text-xl font-semibold text-[#121212] mb-2">Redirecting...</h2>
          <p className="text-[#121212]/60 mb-4">Taking you to your dashboard</p>
          <a 
            href={redirectTo}
            className="text-[#121212] underline font-medium"
          >
            Click here if not redirected
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex font-['Inter']">
      {/* Left Side - Form */}
      <div className="flex-1 flex flex-col justify-center px-8 lg:px-24 bg-white">
        <div className="max-w-md w-full mx-auto">
          <Link to="/" className="inline-flex items-center text-[#121212]/60 hover:text-[#121212] mb-8 transition-colors">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to home
          </Link>
          
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-[#121212] mb-2 font-['Manrope']">Welcome back</h1>
            <p className="text-[#121212]/60">Sign in to access your financial dashboard</p>
          </div>

          <form onSubmit={handleLogin} className="space-y-5">
            <div>
              <Label htmlFor="email" className="text-[#121212]/70 font-medium">Email</Label>
              <div className="relative mt-2">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#121212]/40" />
                <Input 
                  id="email" 
                  type="email" 
                  value={email} 
                  onChange={(e) => setEmail(e.target.value)} 
                  placeholder="you@company.com" 
                  className="pl-12 h-14 rounded-2xl bg-[#F7FFE5] border-2 border-[#BFFF00]/30 focus:border-[#BFFF00] focus:ring-2 focus:ring-[#BFFF00]/20" 
                  data-testid="login-email-input" 
                  required 
                />
              </div>
            </div>
            
            <div>
              <Label htmlFor="password" className="text-[#121212]/70 font-medium">Password</Label>
              <div className="relative mt-2">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#121212]/40" />
                <Input 
                  id="password" 
                  type={showPassword ? "text" : "password"} 
                  value={password} 
                  onChange={(e) => setPassword(e.target.value)} 
                  placeholder="Enter your password" 
                  className="pl-12 pr-12 h-14 rounded-2xl bg-[#F7FFE5] border-2 border-[#BFFF00]/30 focus:border-[#BFFF00] focus:ring-2 focus:ring-[#BFFF00]/20" 
                  data-testid="login-password-input" 
                  required 
                />
                <button 
                  type="button" 
                  onClick={() => setShowPassword(!showPassword)} 
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-[#121212]/40 hover:text-[#121212]"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>
            
            <div className="flex justify-end mt-1">
              <Link 
                to="/forgot-password" 
                className="text-sm text-[#121212]/60 hover:text-[#121212] font-medium transition-colors"
                data-testid="forgot-password-link"
              >
                Forgot password?
              </Link>
            </div>
            
            <Button 
              type="submit" 
              disabled={loading} 
              data-testid="login-submit-btn" 
              className="w-full h-14 rounded-full bg-[#121212] hover:bg-[#2a2a2a] text-white font-semibold text-base mt-6 transition-all hover:scale-[1.02]"
            >
              {loading ? "Signing in..." : "Sign in"}
            </Button>
          </form>

          <p className="mt-8 text-center text-[#121212]/60">
            Don't have an account?{" "}
            <Link to="/register" className="text-[#121212] hover:text-[#121212]/80 font-semibold">Create one</Link>
          </p>
        </div>
      </div>

      {/* Right Side - Branding */}
      <div className="hidden lg:flex flex-1 items-center justify-center p-12 relative overflow-hidden" style={{ background: 'linear-gradient(180deg, #F7FFE5 0%, #EDFFC2 100%)' }}>
        {/* Grid Pattern */}
        <div className="absolute inset-0 opacity-40" style={{ backgroundImage: 'linear-gradient(#BFFF00 1px, transparent 1px), linear-gradient(90deg, #BFFF00 1px, transparent 1px)', backgroundSize: '40px 40px' }} />
        
        <div className="relative max-w-lg text-center">
          <div className="w-24 h-24 bg-[#BFFF00] rounded-3xl flex items-center justify-center mx-auto mb-8 shadow-2xl shadow-[#BFFF00]/30">
            <span className="text-5xl font-extrabold text-[#121212] font-['Manrope']">F</span>
          </div>
          <h2 className="text-4xl font-bold text-[#121212] mb-4 font-['Manrope']">FinAI by Revurge</h2>
          <p className="text-[#121212]/60 text-lg leading-relaxed">Professional bookkeeping and accounting services for creators, founders, and growing businesses.</p>
          
          {/* Stats Badges */}
          <div className="flex flex-wrap justify-center gap-4 mt-8">
            <div className="bg-white/80 backdrop-blur-sm rounded-2xl px-5 py-3 border-2 border-dashed border-[#BFFF00]/50">
              <span className="font-bold text-[#121212]">500+</span>
              <span className="text-[#121212]/60 ml-1">Businesses</span>
            </div>
            <div className="bg-white/80 backdrop-blur-sm rounded-2xl px-5 py-3 border-2 border-dashed border-[#BFFF00]/50">
              <span className="font-bold text-[#121212]">$5M+</span>
              <span className="text-[#121212]/60 ml-1">Tax Savings</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
